import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import FetchTable from 'app/components/table/FetchTable'
import { withDeleteColumn } from 'app/components/table/TableColumn'
import useDeleteNotificationBeacon from './useDeleteNotificationBeacon'
import * as R from 'ramda'
import { useDefaultLanguage } from 'commons/useDefaultLanguage'
import NotificationBeaconDeleteDialog from './NotificationBeaconDeleteDialog'
import { htmlToPlainText } from 'libs/text'
import MessageAlert from 'app/components/MessageAlert'
import moment from 'moment'
import { DISPLAY_FORMAT_DATE } from 'libs/dateUtils'
import DefaultSwitch from 'app/components/switch/DefaultSwitch'
import { CircularProgress } from '@material-ui/core'
import effects from 'app/effects/effects'
import clsx from 'clsx'
import Recipients from 'app/componentV4/MessageDetail/Recipients'
import { TYPE_EVENT } from 'app/constants/messageType'

export const getTypeTextId = (type) => {
  switch (type) {
    case TYPE_EVENT:
      return 'notification.type.event'
    default:
      return 'notification.type.general'
  }
}

const MAX_WIDTH_DELETE_COLUMN = 80

const NotificationBeaconsTable = ({
  pageInfo,
  onFetchData,
  onClickRow,
  refetchData,
  startFetchData,
  endFetchData,
  showErrorDialogue,
  showPagination,
  defaultPageSize,
  recipients,
}) => {
  const defaultLanguage = useDefaultLanguage()
  const { formatMessage } = useIntl()
  const onDeleteSuccessPromise = () => {
    startFetchData()
    return refetchData().then(endFetchData)
  }

  const generateText = (data) => {
    let text = ''
    let rest = 0
    const maxIndex = 2

    data.forEach((item, index) => {
      if (index + 1 === maxIndex) {
        rest = data.length - maxIndex
      }
      if (index < maxIndex) {
        if (index > 0) {
          text = text + ', '
        }
        text = text + item
      }
    })

    if (rest > 0) {
      text = `${text} ${formatMessage({
        id: 'label.and.more',
      })} ${rest} ${formatMessage({ id: 'label.others' })}`
    }
    return text
  }

  const getBeacon = (beacons) => {
    if (R.isEmpty(beacons)) {
      return '-'
    }

    const beaconNames = R.compose(
      R.map((item) => item.name),
      R.sortBy(R.prop('name')),
    )(beacons)
    return generateText(beaconNames)
  }

  const {
    afterConfirmPending,
    pendingDisplayItems,
    onConfirmDelete,
    onCancelDelete,
    handlerDeleteOne,
    isOpenDeleteConfirmation,
  } = useDeleteNotificationBeacon(onDeleteSuccessPromise, showErrorDialogue)
  return (
    <div className="h-full overflow-hidden flex flex-col flex-auto">
      <FetchTable
        onClickRow={(row) => onClickRow(row.id, recipients, refetchData)}
        FilterComponent={null}
        data={pageInfo.data}
        total={pageInfo.totalData}
        pages={pageInfo.total}
        defaultPageSize={defaultPageSize}
        manual
        error={pageInfo.error}
        refetchData={refetchData}
        loading={pageInfo.isLoading}
        onFetchData={onFetchData}
        noDataText={formatMessage({
          id: 'beaconNotificationList.table.empty.label',
        })}
        columns={[
          {
            Header: (
              <div>
                {formatMessage({ id: 'notificationBeacon.table.no.label' })}
              </div>
            ),
            id: 'index',
            width: 50,
            sortable: false,
            className: clsx('sticky fixBackground'),
            Cell: (row) => {
              return (
                <div>{pageInfo.pageSize * pageInfo.page + row.index + 1}</div>
              )
            },
          },
          {
            Header: formatMessage({
              id: 'message.title.label',
            }),
            accessor: 'title',
            id: 'title',
            minWidth: 200,
            type: 'text',
            Cell: (props) => {
              const translation = props.original.translation
              const text = translation.title
              return <div className="truncate">{htmlToPlainText(text)}</div>
            },
          },
          {
            Header: formatMessage({
              id: 'notificationBeacon.table.beaconName.label',
            }),
            accessor: 'beacon',
            id: 'beacon',
            filterable: true,
            sortable: false,
            type: 'text',
            Cell: (props) => {
              return getBeacon(R.path(['original', 'beacons'], props))
            },
          },
          {
            Header: formatMessage({
              id: 'text.type',
            }),
            accessor: 'types',
            id: 'types',
            filterable: true,
            sortable: false,
            type: 'text',
            Cell: (props) => {
              return formatMessage({
                id: getTypeTextId(R.path(['original', 'type'], props)),
              })
            },
          },
          {
            Header: formatMessage({
              id: 'notificationBeacon.table.recipients.label',
            }),
            accessor: 'groups',
            filterable: true,
            sortable: false,
            type: 'text',
            Cell: (props) => {
              return (
                <Recipients
                  groups={R.path(['original', 'groups'], props)}
                  recipients={R.path(['original', 'recipients'], props)}
                  channels={R.path(['original', 'channels'], props)}
                  isDraft={false}
                  isScheduled={false}
                  isTooltip={true}
                />
              )
            },
          },
          {
            Header: formatMessage({
              id: 'notificationBeacon.table.lastUpdated.label',
            }),
            accessor: 'updatedAt',
            id: 'updatedAt',
            minWidth: 120,
            type: 'text',
            Cell: (props) => {
              return moment(props.original.updatedAt).format(
                DISPLAY_FORMAT_DATE,
              )
            },
          },
          {
            Header: formatMessage({
              id: 'notificationBeacon.table.active.label',
            }),
            accessor: 'isActive',
            filterable: false,
            sortable: false,
            minWidth: 128,
            Cell: (props) => (
              <AsyncSwitch
                {...props}
                onChange={async () => {
                  await effects.apis.updateStatusNotificationBeacon({
                    notificationId: props.original.id,
                    isActive: Number(!props.original.isActive),
                  })
                  await refetchData()
                }}
              />
            ),
          },
          withDeleteColumn(
            handlerDeleteOne,
            true,
            undefined,
            false,
            MAX_WIDTH_DELETE_COLUMN,
          ),
        ]}
        showPagination={showPagination}
      />
      <NotificationBeaconDeleteDialog
        isPending={afterConfirmPending}
        beaconToBeDeletedArray={pendingDisplayItems}
        open={isOpenDeleteConfirmation}
        onConfirm={onConfirmDelete}
        onCancel={onCancelDelete}
      />
    </div>
  )
}

export default NotificationBeaconsTable

const AsyncSwitch = (props) => {
  const { onChange } = props
  const { formatMessage } = useIntl()
  const reduxDispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  return (
    <>
      <DefaultSwitch
        checked={Boolean(props.original.isActive)}
        onClick={(event) => {
          event.stopPropagation()
        }}
        onChange={async () => {
          setIsLoading(true)
          try {
            await onChange()
          } catch (error) {
            reduxDispatch(MessageAlert.showNetworkError(formatMessage)(error))
          } finally {
            setIsLoading(false)
          }
        }}
      />
      {isLoading && (
        <CircularProgress size={24} className="ltr:ml-8 rtl:mr-8" />
      )}
    </>
  )
}
